import React from 'react'
import { Button, DatePicker, Modal, Radio, Space, Spin, Table, Tag, Typography } from 'antd'
import {
  ESellerTransactionsType,
  ITransaction,
  TWithdrawalTransactionStatus
} from 'models/transaction'
import moment from 'moment'
import { FilterFilled } from '@ant-design/icons'
import { ColumnsType, TableProps } from 'antd/es/table'
import { withdrawalRequestsApi } from 'api/withdrawalRequests'
import { transactionsApi } from 'api/transaction'
import { MobileFilterTitle } from 'components/ui/MobileFilterTitle/MobileFilterTitle'
import { OrderModal } from 'components/orders/OrderModal/OrderModal'
import { WithdrawalRequestDetails } from 'components/transactions/WithdrawalRequestDetails/WithdrawalRequestDetails'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { IDateFilter } from 'models/app'
import { IWithdrawalRequest } from 'models/withdrawalRequest'
import { getTransactionTypeName } from 'shared/utils/transactions/getTransactionTypeName'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import {
  getWithdrawalTransactionStatusColor,
  getWithdrawalTransactionStatusLabel,
  withdrawalTransactionStatusFilterOptions
} from 'shared/constants/withdrawals'
import localeRu from 'antd/es/date-picker/locale/ru_RU'
import styles from './TransactionsTable.module.scss'
import 'dayjs/locale/ru'

interface ITransactionsTableProps {
  sellerId: string
  style?: React.CSSProperties
  isNeedToUpdate?: string
  showWithdrawalModal?: boolean
  // true — использовать admin-эндпоинт для загрузки заявки (admin/withdrawal-requests/:id)
  useAdminWithdrawalApi?: boolean
}

export const TransactionsTable: React.FC<ITransactionsTableProps> = React.memo(({ sellerId, style, isNeedToUpdate, showWithdrawalModal, useAdminWithdrawalApi }) => {
  const isMobile = useIsMobile()
  const [transactions, setTransactions] = React.useState<ITransaction[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  const [withdrawalModalRequest, setWithdrawalModalRequest] = React.useState<IWithdrawalRequest | null>(null)
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = React.useState(false)
  const [isWithdrawalModalLoading, setIsWithdrawalModalLoading] = React.useState(false)
  const [orderModalId, setOrderModalId] = React.useState<string | null>(null)

  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const pageSize = 8

  const [type, setType] = React.useState<ESellerTransactionsType | undefined>(undefined)
  const [status, setStatus] = React.useState<TWithdrawalTransactionStatus | undefined>(undefined)
  const [sum, setSum] = React.useState<'asc' | 'desc' | undefined>(undefined)
  const [sortDate, setSortData] = React.useState<IDateFilter | null>(null)

  const [datePickerValue, setDatePickerValue] = React.useState<Parameters<NonNullable<React.ComponentProps<typeof DatePicker.RangePicker>['onChange']>>[0]>(null)

  const getTransactions = async (skip: number) => {
    try {
      setIsLoading(true)
      const response = await transactionsApi.getTransactions({
        skip: String(skip),
        limit: String(pageSize),
        seller_id: sellerId,
        type,
        // Массив, т.к. статус отмены хранится в базе в двух написаниях («ё» и «е»)
        status: status
          ? withdrawalTransactionStatusFilterOptions.find((option) => option.value === status)?.requestStatuses
          : undefined,
        sum,
        from: sortDate?.from ?? '',
        to: sortDate?.to ?? ''
      })
      setTotal(response.data.count)
      setTransactions(response.data.transactions)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onPageChange = (current: number) => {
    getTransactions((current - 1) * pageSize)
    setCurrentPage(current)
  }

  React.useEffect(() => {
    getTransactions(0)
    setCurrentPage(1)
  }, [type, status, sum, sortDate, isNeedToUpdate])

  const columns: ColumnsType<ITransaction> = React.useMemo(
    () => [
      {
        title: 'Тип транзакции',
        dataIndex: 'type',
        width: '24%',
        render: (_, { type }) => {
          return <Typography.Text>{getTransactionTypeName(type)}</Typography.Text>
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Radio.Group
                onChange={(e) => {
                  setType(e.target.value)
                  close()
                }}
                value={type}
                style={{ display: 'block', marginBottom: 15 }}
              >
                <Space direction='vertical' size={15}>
                  {(Object.keys(ESellerTransactionsType) as Array<keyof typeof ESellerTransactionsType>).map((item) => (
                    <Radio key={item} value={item}>
                      <Typography.Text>{getTransactionTypeName(item)}</Typography.Text>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              <Button
                onClick={() => {
                  setType(undefined)
                  close()
                }}
                size='small'
                className='filterButton'
              >
                Очистить
              </Button>
              <Button
                type='link'
                size='small'
                onClick={() => {
                  close()
                }}
              >
                Закрыть
              </Button>
            </div>
          )
        },
        filterIcon: () => (
          <FilterFilled
            style={{
              color: type ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
            }}
          />
        )
      },
      {
        title: 'Номер заказа',
        dataIndex: 'order_number',
        width: '16%',
        render: (_, { order_number }) => {
          return <Typography.Text>{order_number ?? '—'}</Typography.Text>
        }
      },
      {
        title: 'Сумма',
        dataIndex: 'sum',
        width: '18%',
        sorter: true,
        render: (_, { sum, credited_sum, type }) => {
          // credited_sum — сумма для журнала, не меняется после возвратов в отличие от sum
          // Вывод без знака — в оборот не входит
          if (type === ESellerTransactionsType.SALE) {
            return <Typography.Text type='success'>{`+${getNormalizedPrice(credited_sum ?? sum)}`}</Typography.Text>
          }
          // Возврат и штраф — красные с минусом: оба вычитаются из оборота
          if (type === ESellerTransactionsType.REFUND || type === ESellerTransactionsType.FINE) {
            return <Typography.Text type='danger'>{getNormalizedPrice(sum)}</Typography.Text>
          }
          if (type === ESellerTransactionsType.WITHDRAW) {
            return <Typography.Text>{getNormalizedPrice(Math.abs(sum))}</Typography.Text>
          }
          return <Typography.Text>{getNormalizedPrice(sum)}</Typography.Text>
        }
      },
      {
        title: 'Дата',
        dataIndex: 'date',
        width: '24%',
        render: (_, { createdAt }) => {
          return (
            <Typography.Text>{moment(createdAt).locale('ru').format('LL')}</Typography.Text>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div className='filterWrapper'>
              <DatePicker.RangePicker
                rootClassName='myRangePicker'
                value={datePickerValue}
                format='DD/MM/YYYY'
                locale={{
                  ...localeRu,
                  lang: {
                    ...localeRu.lang,
                    rangePlaceholder: ['От', 'До']
                  }
                }}
                onCalendarChange={(value) => {
                  setDatePickerValue(value)
                  if (!value?.[0] && !value?.[1]) {
                    setSortData(null)
                  } else {
                    setSortData({
                      from: value?.[0]?.hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss') ?? '',
                      to: value?.[1]?.hour(23).minute(59).second(59).format('YYYY-MM-DDTHH:mm:ss') ?? ''
                    })
                  }
                }}
                style={{ width: 200, borderRadius: 4 }}
                inputReadOnly
              />
              <div className={styles.dateButtons}>
                <Button
                  onClick={() => {
                    setSortData(null)
                    setDatePickerValue(null)
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        filterIcon: () => (
          <FilterFilled
            style={{
              color: sortDate ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
            }}
          />
        )
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: '12%',
        render: (_, { status }) => {
          return status
            ? <Tag color={getWithdrawalTransactionStatusColor(status)}>{getWithdrawalTransactionStatusLabel(status)}</Tag>
            : <Typography.Text>—</Typography.Text>
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Radio.Group
                onChange={(e) => {
                  setStatus(e.target.value)
                  close()
                }}
                value={status}
                style={{ display: 'block', marginBottom: 15 }}
              >
                <Space direction='vertical' size={15}>
                  {withdrawalTransactionStatusFilterOptions.map((option) => (
                    <Radio key={option.value} value={option.value}>
                      <Tag color={getWithdrawalTransactionStatusColor(option.value)}>
                        {getWithdrawalTransactionStatusLabel(option.value)}
                      </Tag>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              <Button
                onClick={() => {
                  setStatus(undefined)
                  close()
                }}
                size='small'
                className='filterButton'
              >
                Очистить
              </Button>
              <Button
                type='link'
                size='small'
                onClick={() => {
                  close()
                }}
              >
                Закрыть
              </Button>
            </div>
          )
        },
        filterIcon: () => (
          <FilterFilled
            style={{
              color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
            }}
          />
        )
      }
    ],
    [type, status, sortDate, datePickerValue]
  )

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<ITransaction> = React.useMemo(() => {
    const findColumn = (dataIndex: string) =>
      columns.find((column) => (column as any).dataIndex === dataIndex)

    const typeColumn = findColumn('type')
    const orderColumn = findColumn('order_number')
    const dateColumn = findColumn('date')
    const sumColumn = findColumn('sum')
    const statusColumn = findColumn('status')

    return [
      {
        ...typeColumn,
        title: (
          <MobileFilterTitle
            title='Транзакция'
            columns={[typeColumn, dateColumn, statusColumn]}
            isActive={Boolean(type || status || sortDate)}
          />
        ),
        width: '58%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileType}>
              {typeColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            {!!record.order_number && (
              <span className={styles.mobileSubText}>
                Заказ{' '}
                {orderColumn?.render?.(value, record, index) as React.ReactNode}
              </span>
            )}
            <span className={styles.mobileSubText}>
              {dateColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
          </div>
        )
      },
      {
        ...sumColumn,
        title: 'Сумма',
        width: '42%',
        render: (value, record, index) => (
          <div className={styles.mobileSumCell}>
            {sumColumn?.render?.(value, record, index) as React.ReactNode}
            {statusColumn?.render?.(value, record, index) as React.ReactNode}
          </div>
        )
      }
    ]
  }, [columns, type, status, sortDate])

  const onChange: TableProps<ITransaction>['onChange'] = (_, filters, sort) => {
    const sorter = Array.isArray(sort) ? sort[0] : sort

    setSum(
      sorter.order === 'ascend'
        ? 'asc'
        : sorter.order === 'descend'
          ? 'desc'
          : undefined
    )
  }

  const openWithdrawalModal = async (transaction: ITransaction) => {
    if (!transaction.withdrawal_request_id) {
      showMessage('info', 'Нет данных', 'Для этой выплаты заявка не найдена')
      return
    }
    setIsWithdrawalModalOpen(true)
    setIsWithdrawalModalLoading(true)
    try {
      const fetch = useAdminWithdrawalApi
        ? withdrawalRequestsApi.getById(transaction.withdrawal_request_id)
        : withdrawalRequestsApi.getMyById(transaction.withdrawal_request_id)
      const detailResponse = await fetch
      setWithdrawalModalRequest(detailResponse.data)
    } catch (e) {
      showServerError(e)
      setIsWithdrawalModalOpen(false)
    } finally {
      setIsWithdrawalModalLoading(false)
    }
  }

  const closeWithdrawalModal = () => {
    setIsWithdrawalModalOpen(false)
    setWithdrawalModalRequest(null)
  }

  const getValidOrderId = (orderId?: string | null) => {
    if (!orderId || orderId === 'undefined') {
      return null
    }

    return orderId
  }

  const openOrder = (orderId?: string | null) => {
    const validOrderId = getValidOrderId(orderId)

    if (!validOrderId) {
      return
    }

    setOrderModalId(validOrderId)
  }

  return (
    <div style={style}>
      <Typography.Title level={2} className={styles.title}>
        Транзакции
      </Typography.Title>
      <Table
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={transactions}
        loading={isLoading}
        rowKey={(el) => el._id}
        showSorterTooltip={false}
        onRow={(transaction) => {
          if (showWithdrawalModal && transaction.type === ESellerTransactionsType.WITHDRAW) {
            return {
              onClick: () => openWithdrawalModal(transaction),
              style: { cursor: 'pointer' }
            }
          }

          const validOrderId = getValidOrderId(transaction.order_id)
          return {
            onClick: () => openOrder(validOrderId),
            style: { cursor: validOrderId ? 'pointer' : 'default' }
          }
        }}
        onChange={onChange}
        pagination={{
          current: currentPage,
          onChange: onPageChange,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
      <Modal
        centered
        open={isWithdrawalModalOpen}
        footer={null}
        onCancel={closeWithdrawalModal}
        width={860}
      >
        {isWithdrawalModalLoading && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin size='large' />
          </div>
        )}
        {!isWithdrawalModalLoading && withdrawalModalRequest && (
          <>
            <WithdrawalRequestDetails request={withdrawalModalRequest} />
            <div className={styles.withdrawalModalFooter}>
              <Button onClick={closeWithdrawalModal}>Закрыть</Button>
            </div>
          </>
        )}
      </Modal>
      <OrderModal orderId={orderModalId} onClose={() => setOrderModalId(null)} />
    </div>
  )
})
